import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Box,
  Typography,
  TextField,
  Skeleton,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Snackbar,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import AppButton from "../ui/AppButton";
import AppSelect from "../ui/AppSelect";
import DataTable from "../ui/Table";
import CapacityStatusChip from "../ui/CapacityStatusChip";
import DeleteDialog from "../modals/DeleteDialog";
import { getEmployees } from "../services/employeeService";
import { getProjects, getProject, updateProject, getCapacitySummary } from "../services/projectService";
import CapacityCalendar from "../components/projects/CapacityCalendar";
import AssignProjectDialog from "../components/projects/AssignProjectDialog";
import EditCapacityDialog from "../components/projects/EditCapacityDialog";
import { useUserRole } from "../context/UserRoleContext";
import { useDepartments } from "../context/DepartmentsContext";
import { canAccessPage, canWritePage, hasTeamDataScope } from "../auth/roles";
import {
  RESOURCE_PROJECT_SKELETON_COLUMNS,
  RESOURCE_TEAM_OVERVIEW_SKELETON_COLUMNS
} from "../constants/resourceManagementColumns";

// Employees below this total capacity % are considered "on the bench" (under-allocated).
const BENCH_CAPACITY_THRESHOLD = 50;

function ResourceManagement() {
  const { role, employee } = useUserRole();
  const { departments } = useDepartments();
  const canManageResources = canWritePage(role, "resource_management");
  const isReadOnlyViewer = !canManageResources;

  const [searchParams] = useSearchParams();

  // Pre-select employee and switch to detail tab when navigated from another page.
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(
    () => { const id = searchParams.get("employee"); return id ? Number(id) : null; }
  );
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get("employee") ? 1 : 0
  );
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [historicalProjects, setHistoricalProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [capacitySummary, setCapacitySummary] = useState({});
  const [allProjects, setAllProjects] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [benchOnly, setBenchOnly] = useState(false);
  const [teamSearch, setTeamSearch] = useState("");

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoading(true);

        // Read-only roles (e.g. Employee/Member) can only view their own capacity.
        if (!canManageResources) {
          if (employee) {
            setEmployees([employee]);
            setSelectedEmployeeId(employee.employee_id);
          }
          return;
        }

        let managerId = null;
        if (hasTeamDataScope(role, "resource_management")) {
          managerId = employee?.employee_id ?? -1;
        }

        const data = await getEmployees(search, managerId, null);
        setEmployees(data);

        if (data.length > 0 && !selectedEmployeeId) {
          setSelectedEmployeeId(data[0].employee_id);
        }
      } catch (error) {
        console.error("Error loading employees:", error);
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, [search, role, employee, selectedEmployeeId, canManageResources]);

  useEffect(() => {
    if (!canManageResources) {
      return;
    }

    getCapacitySummary()
      .then(setCapacitySummary)
      .catch((error) => console.error("Error loading capacity summary:", error));
  }, [canManageResources, refreshKey]);

  useEffect(() => {
    if (!canManageResources) {
      return;
    }

    getProjects()
      .then(setAllProjects)
      .catch((error) => console.error("Error loading projects:", error));
  }, [canManageResources, refreshKey]);

  const loadEmployeeProjects = useCallback(async () => {
    if (!selectedEmployeeId) {
      setEmployeeProjects([]);
      setHistoricalProjects([]);
      return;
    }

    try {
      setLoadingProjects(true);
      const data = await getProjects("", selectedEmployeeId, true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const active = data.filter(
        (p) => !p.assignment_end_date || new Date(p.assignment_end_date) >= today
      );
      const historical = data.filter(
        (p) => p.assignment_end_date && new Date(p.assignment_end_date) < today
      );
      setEmployeeProjects(active);
      setHistoricalProjects(historical);
      setSelectedProjectIds((prev) => prev.filter((id) => active.some((p) => p.project_id === id)));
    } catch (error) {
      console.error("Error loading employee projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  }, [selectedEmployeeId]);

  useEffect(() => {
    loadEmployeeProjects();
  }, [loadEmployeeProjects]);

  const handleProjectAssigned = () => {
    loadEmployeeProjects();
    setRefreshKey((prev) => prev + 1);
  };

  const handleRemoveProject = async () => {
    if (!removeTarget || isRemoving) {
      return;
    }

    try {
      setIsRemoving(true);

      const project = await getProject(removeTarget.project.project_id);

      await updateProject(removeTarget.project.project_id, {
        project_name: project.project_name,
        description: project.description,
        start_date: project.start_date,
        end_date: project.end_date,
        assigned_to: project.assigned_to.filter((a) => a.employee_id !== removeTarget.employeeId)
      });

      setRemoveTarget(null);
      loadEmployeeProjects();
      setRefreshKey((prev) => prev + 1);

      setSnackbar({ open: true, message: "Project removed successfully", severity: "success" });
    } catch (error) {
      console.error("Error removing project:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to remove project",
        severity: "error"
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleBulkRemoveProjects = async () => {
    if (selectedProjectIds.length === 0 || isBulkRemoving || !selectedEmployeeId) {
      return;
    }

    try {
      setIsBulkRemoving(true);

      let successCount = 0;
      let failedCount = 0;

      for (const projectId of selectedProjectIds) {
        try {
          const project = await getProject(projectId);

          await updateProject(projectId, {
            project_name: project.project_name,
            description: project.description,
            start_date: project.start_date,
            end_date: project.end_date,
            assigned_to: project.assigned_to.filter((a) => a.employee_id !== selectedEmployeeId)
          });

          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkRemoveOpen(false);
      setSelectedProjectIds([]);
      loadEmployeeProjects();
      setRefreshKey((prev) => prev + 1);

      if (failedCount === 0) {
        setSnackbar({ open: true, message: `${successCount} project(s) removed successfully`, severity: "success" });
      } else {
        setSnackbar({ open: true, message: `${successCount} removed, ${failedCount} failed`, severity: "warning" });
      }
    } catch (error) {
      console.error("Error removing projects:", error);
      setBulkRemoveOpen(false);
      setSnackbar({ open: true, message: "Bulk remove failed", severity: "error" });
    } finally {
      setIsBulkRemoving(false);
    }
  };

  const teamOverviewRows = employees.filter((emp) => {
    if (teamSearch && !emp.employee_name?.toLowerCase().includes(teamSearch.trim().toLowerCase())) {
      return false;
    }

    if (departmentFilter && emp.department_id !== departmentFilter) {
      return false;
    }

    if (projectFilter) {
      const project = allProjects.find((p) => p.project_id === projectFilter);
      if (!project || !project.assigned_to.some((a) => a.employee_id === emp.employee_id)) {
        return false;
      }
    }

    if (benchOnly && (capacitySummary[emp.employee_id]?.total_capacity_percent || 0) >= BENCH_CAPACITY_THRESHOLD) {
      return false;
    }

    return true;
  });

  const getEmployeeRemark = (empId) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const empProjects = allProjects.filter((p) =>
      p.assigned_to.some((a) => a.employee_id === empId)
    );

    if (empProjects.length === 0) return { text: "-", weeks: null };

    let maxEndDate = null;
    for (const p of empProjects) {
      if (p.end_date) {
        const end = new Date(p.end_date);
        if (!maxEndDate || end > maxEndDate) maxEndDate = end;
      }
    }

    if (!maxEndDate || maxEndDate < today) return { text: "-", weeks: null };

    const weeks = Math.ceil((maxEndDate - today) / (7 * 24 * 60 * 60 * 1000));
    const text = weeks === 1 ? "Booked for next 1 week" : `Booked for next ${weeks} weeks`;
    return { text, weeks };
  };

  const handleExportCsv = () => {
    const header = ["Employee", "Designation", "Department", "Projects", "Total Capacity %", "Status", "Remark"];
    const rows = teamOverviewRows.map((emp) => {
      const capacity = capacitySummary[emp.employee_id]?.total_capacity_percent || 0;
      const status = capacity >= 100 ? "Fully Booked" : capacity > 0 ? "Allocated" : "Not Allocated";

      return [
        emp.employee_name,
        emp.designation || "",
        emp.department_name || "",
        capacitySummary[emp.employee_id]?.project_count || 0,
        capacity,
        status,
        getEmployeeRemark(emp.employee_id).text
      ];
    });

    const csvContent = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `team-capacity-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading && employees.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: "100vh",
          p: 3
        }}
      >
        <Skeleton variant="text" width={260} height={40} />
        <Skeleton variant="text" width={340} height={22} sx={{ mb: 3 }} />

        {!isReadOnlyViewer && (
          <Box display="flex" gap={3} mb={3}>
            <Skeleton variant="text" width={100} height={36} />
            <Skeleton variant="text" width={130} height={36} />
          </Box>
        )}

        {!isReadOnlyViewer ? (
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Skeleton variant="text" width={220} height={28} sx={{ mb: 1.5 }} />

              <DataTable
                columns={RESOURCE_TEAM_OVERVIEW_SKELETON_COLUMNS}
                rows={[]}
                loading
                rowKey={(emp) => emp.employee_id}
                pagination={false}
              />
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Skeleton variant="text" width={160} height={28} />
                  <Skeleton variant="text" width={120} height={20} />
                </Box>
              </Box>

              <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2, mb: 3 }} />

              <Skeleton variant="text" width={180} height={28} sx={{ mb: 1.5 }} />

              <DataTable
                columns={RESOURCE_PROJECT_SKELETON_COLUMNS.filter((column) => column.key !== "actions")}
                rows={[]}
                loading
                rowKey={(project) => project.project_id}
                pagination={false}
              />
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  if (!canAccessPage(role, "resource_management")) {
    return (
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: "100vh",
          p: 3
        }}
      >
        <Alert severity="info">
          Only Managers and Admins can access resource management
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3
      }}
    >
      <Box mb={3}>
        <Typography variant="h4" fontWeight={600} color="text.primary" mb={0.5}>
          Resource Management
        </Typography>

        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          View and manage employee capacity allocation across projects
        </Typography>
      </Box>

      {!isReadOnlyViewer && (
        <Tabs
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          sx={{ mb: 3, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Tab label="Team Overview" />
          <Tab label="Employee Details" />
        </Tabs>
      )}

      {!isReadOnlyViewer && activeTab === 0 && employees.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Team Capacity Overview
              </Typography>

              <AppButton
                size="small"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                onClick={handleExportCsv}
                disabled={teamOverviewRows.length === 0}
                sx={{ height: "auto" }}
              >
                Export CSV
              </AppButton>
            </Box>

            <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search employees"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "#94a3b8" }} />
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper"
                    }
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 220 }}>
                <AppSelect
                  placeholder="All departments"
                  isClearable
                  options={departments.map((dept) => ({ value: dept.department_id, label: dept.department_name }))}
                  value={departmentFilter || null}
                  onChange={(value) => setDepartmentFilter(value || "")}
                />
              </Box>

              <Box sx={{ minWidth: 220 }}>
                <AppSelect
                  placeholder="All projects"
                  isClearable
                  options={allProjects.map((project) => ({ value: project.project_id, label: project.project_name }))}
                  value={projectFilter || null}
                  onChange={(value) => setProjectFilter(value || "")}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={benchOnly}
                    onChange={(e) => setBenchOnly(e.target.checked)}
                  />
                }
                label={`Bench only (<${BENCH_CAPACITY_THRESHOLD}%)`}
                sx={{ ml: 0, "& .MuiFormControlLabel-label": { fontSize: 13, color: "text.secondary" } }}
              />
            </Box>

            <DataTable
              columns={[
                {
                  key: "employee_name",
                  label: "Employee",
                  width: "23%",
                  render: (emp) => (
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        src={emp.profile_picture || undefined}
                        sx={{ width: 34, height: 34, fontSize: 14 }}
                      >
                        {emp.employee_name?.charAt(0)}
                      </Avatar>

                      <Box>
                        <Typography fontSize={13} fontWeight={600}>
                          {emp.employee_name}
                        </Typography>

                        <Typography fontSize={12} color="#94a3b8">
                          {emp.designation || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  )
                },
                {
                  key: "department_name",
                  label: "Department",
                  width: "20%",
                  render: (emp) => (
                    <Typography fontSize={13} color="text.secondary">
                      {emp.department_name || "No Department"}
                    </Typography>
                  )
                },
                {
                  key: "project_count",
                  label: "Projects",
                   width: "10%",
                  render: (emp) => (
                    <Typography fontSize={13} color="text.secondary">
                      {capacitySummary[emp.employee_id]?.project_count || 0}
                    </Typography>
                  )
                },
                {
                  key: "total_capacity_percent",
                  label: "Total Capacity",
                  width: "10%",

                  render: (emp) => {
                    const capacity = capacitySummary[emp.employee_id]?.total_capacity_percent || 0;
                    const isOverallocated = capacity > 100;

                    return (
                      <Box display="flex" alignItems="center" gap={0.6}>
                        <Chip
                          label={`${capacity}%`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            background: isOverallocated ? "#fee2e2" : "#eff6ff",
                            color: isOverallocated ? "#dc2626" : "#2563eb"
                          }}
                        />

                        {isOverallocated && (
                          <Tooltip title={`Overallocated by ${capacity - 100}% across overlapping projects`}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#dc2626" }} />
                          </Tooltip>
                        )}
                      </Box>
                    );
                  }
                },
                {
                  key: "status",
                  label: "Status",
                  width: "12%",
                  render: (emp) => (
                    <CapacityStatusChip capacityPercent={capacitySummary[emp.employee_id]?.total_capacity_percent} />
                  )
                },
                {
                  key: "remark",
                  label: "Remark",
                   width: "15%",
                  render: (emp) => {
                    const { text, weeks } = getEmployeeRemark(emp.employee_id);
                    if (weeks === 1) {
                      return (
                        <Chip
                          icon={<WarningAmberRoundedIcon sx={{ fontSize: "14px !important" }} />}
                          label={text}
                          size="small"
                          sx={{
                            bgcolor: "#fff7ed",
                            color: "#c2410c",
                            border: "1px solid #fed7aa",
                            fontWeight: 600,
                            fontSize: 11
                          }}
                        />
                      );
                    }
                    return (
                      <Typography fontSize={13} color="text.secondary">
                        {text}
                      </Typography>
                    );
                  }
                },
                {
                  key: "actions",
                  label: "Actions",
                  width: "7%",
                  align: "left",
                  render: (emp) => (
                    <Box display="flex" justifyContent="flex-start">
                      <Tooltip title="Edit Capacity">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEmployeeId(emp.employee_id);
                            setActiveTab(1);
                          }}
                          sx={{ background: "#eff6ff", width: 32, height: 32 }}
                        >
                          <EditIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
              ]}
              rows={teamOverviewRows}
              tableLayout="fixed"
              rowKey={(emp) => emp.employee_id}
              onRowClick={(emp) => {
                setSelectedEmployeeId(emp.employee_id);
                setActiveTab(1);
              }}
              pagination={teamOverviewRows.length > 10}
              rowsPerPage={10}
              emptyState={{ title: "No employees match the selected filters" }}
            />
          </CardContent>
        </Card>
      )}

      {(isReadOnlyViewer || activeTab === 1) && (
        <>
          {!isReadOnlyViewer && (
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search employees"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "#94a3b8" }} />
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper"
                    }
                  }}
                />
              </Box>

              {employees.length > 0 && (
                <Box sx={{ minWidth: 250 }}>
                  <AppSelect
                    options={employees.map((emp) => ({ value: emp.employee_id, label: emp.employee_name }))}
                    value={selectedEmployeeId}
                    onChange={(value) => setSelectedEmployeeId(value)}
                    withAvatar
                    placeholder="Select employee"
                  />
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {(isReadOnlyViewer || activeTab === 1) && selectedEmployeeId && (
        <Box>
          {employees
            .filter((emp) => emp.employee_id === selectedEmployeeId)
            .map((emp) => (
              <Card key={emp.employee_id} sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {emp.employee_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {emp.department_name || "No Department"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1} alignItems="center">
                      {!isReadOnlyViewer && (
                        <>
                          <AppButton
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            loading={isBulkRemoving}
                            onClick={() => setBulkRemoveOpen(true)}
                            disabled={selectedProjectIds.length === 0}
                            sx={{ height: "auto" }}
                          >
                            {isBulkRemoving ? "Removing..." : `Delete All (${selectedProjectIds.length})`}
                          </AppButton>

                          <AppButton
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setAssignDialogOpen(true)}
                            sx={{ height: "auto" }}
                          >
                            Assign Project
                          </AppButton>
                        </>
                      )}
                    </Box>
                  </Box>

                  <CapacityCalendar employeeId={emp.employee_id} refreshKey={refreshKey} />

                  <Box mt={3}>
                    <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                      Assigned Projects
                    </Typography>

                    <DataTable
                      columns={[
                        {
                          key: "project_name",
                          label: "Project Name",
                          render: (project) => (
                            <Typography fontSize={13} fontWeight={600}>
                              {project.project_name}
                            </Typography>
                          )
                        },
                        {
                          key: "capacity_percent",
                          label: "Capacity %",
                          render: (project) => (
                            <Chip
                              label={`${project.capacity_percent}%`}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                background: "#eff6ff",
                                color: "#2563eb"
                              }}
                            />
                          )
                        },
                        {
                          key: "start_date",
                          label: "Start Date",
                          render: (project) => (
                            <Typography fontSize={13} color="text.secondary">
                              {project.start_date || "-"}
                            </Typography>
                          )
                        },
                        {
                          key: "end_date",
                          label: "End Date",
                          render: (project) => (
                            <Typography fontSize={13} color="text.secondary">
                              {project.end_date || "-"}
                            </Typography>
                          )
                        },
                        {
                          key: "status",
                          label: "Status",
                          render: (project) => <CapacityStatusChip capacityPercent={project.capacity_percent} />
                        },
                        ...(!isReadOnlyViewer ? [{
                          key: "actions",
                          label: "Actions",
                          width: 100,
                          render: (project) => (
                            <Box display="flex" justifyContent="flex-end" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => setEditTarget({ project, employee: emp })}
                                sx={{ background: "#eff6ff" }}
                              >
                                <EditIcon fontSize="small" sx={{ color: "#2563eb" }} />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => setRemoveTarget({
                                  project,
                                  employeeId: emp.employee_id,
                                  employeeName: emp.employee_name
                                })}
                                sx={{ background: "#fee2e2" }}
                              >
                                <DeleteIcon fontSize="small" sx={{ color: "#dc2626" }} />
                              </IconButton>
                            </Box>
                          )
                        }] : [])
                      ]}
                      rows={employeeProjects}
                      rowKey={(project) => project.project_id}
                      loading={loadingProjects}
                      pagination={false}
                      selectable={!isReadOnlyViewer}
                      selectedIds={selectedProjectIds}
                      onSelectionChange={setSelectedProjectIds}
                      emptyState={{ title: "No projects assigned" }}
                    />

                    {historicalProjects.length > 0 && (
                      <Box mt={3}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1.5}>
                          Past Assignments
                        </Typography>

                        <DataTable
                          columns={[
                            {
                              key: "project_name",
                              label: "Project Name",
                              render: (project) => (
                                <Typography fontSize={13} fontWeight={600} color="text.secondary">
                                  {project.project_name}
                                </Typography>
                              )
                            },
                            {
                              key: "capacity_percent",
                              label: "Capacity %",
                              render: (project) => (
                                <Chip
                                  label={`${project.capacity_percent}%`}
                                  size="small"
                                  sx={{ fontWeight: 600, background: "#f1f5f9", color: "#64748b" }}
                                />
                              )
                            },
                            {
                              key: "assignment_start_date",
                              label: "From",
                              render: (project) => (
                                <Typography fontSize={13} color="text.secondary">
                                  {project.assignment_start_date || project.start_date || "-"}
                                </Typography>
                              )
                            },
                            {
                              key: "assignment_end_date",
                              label: "To",
                              render: (project) => (
                                <Typography fontSize={13} color="text.secondary">
                                  {project.assignment_end_date || "-"}
                                </Typography>
                              )
                            },
                            {
                              key: "status",
                              label: "Status",
                              render: () => (
                                <Chip label="Ended" size="small" variant="outlined" sx={{ color: "text.secondary", borderColor: "divider" }} />
                              )
                            }
                          ]}
                          rows={historicalProjects}
                          rowKey={(project) => `hist-${project.project_id}`}
                          pagination={false}
                          emptyState={{ title: "" }}
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
        </Box>
      )}

      {employees.length === 0 && (
        <Card sx={{ borderRadius: 3, textAlign: "center", p: 4 }}>
          <Typography color="textSecondary">
            No employees found
          </Typography>
        </Card>
      )}

      <AssignProjectDialog
        open={assignDialogOpen}
        employee={employees.find((emp) => emp.employee_id === selectedEmployeeId) || null}
        onClose={() => setAssignDialogOpen(false)}
        onAssigned={handleProjectAssigned}
      />

      <EditCapacityDialog
        open={Boolean(editTarget)}
        project={editTarget?.project || null}
        employee={editTarget?.employee || null}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          setSnackbar({ open: true, message: "Capacity updated successfully", severity: "success" });
          loadEmployeeProjects();
          setRefreshKey((prev) => prev + 1);
        }}
      />

      <DeleteDialog
        open={Boolean(removeTarget)}
        onClose={() => {
          if (!isRemoving) {
            setRemoveTarget(null);
          }
        }}
        onConfirm={handleRemoveProject}
        isDeleting={isRemoving}
        title="Remove Project"
        message={`Are you sure you want to remove ${removeTarget?.employeeName || "this employee"} from`}
        itemName={removeTarget?.project?.project_name}
        confirmLabel="Remove"
      />

      <DeleteDialog
        open={bulkRemoveOpen}
        onClose={() => {
          if (!isBulkRemoving) {
            setBulkRemoveOpen(false);
          }
        }}
        onConfirm={handleBulkRemoveProjects}
        isDeleting={isBulkRemoving}
        title="Remove Selected Projects"
        message="Are you sure you want to remove this employee from all selected projects?"
        itemName={`Selected: ${selectedProjectIds.length}`}
        confirmLabel="Remove All"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ResourceManagement;
